import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Button, Icon, Select } from '@iq/react-components';
import SimpleModal from '../../../../SimpleModal/SimpleModal';
import ConfirmationDialog from '../../../../ConfirmationDialog/ConfirmationDialog';
import NotificationRulesForm from '../NotificationRulesForm/NotificationRulesForm';
import { getActiveSite } from '../../../../../bundles/sites';
import { createNotificationConfig } from '../../../../../bundles/notification-config';
import { DEFAULT_STATE_CHANGE_SETTINGS, isConfigCustom } from '../../constants';

const AddCustomizationModal = ({ siteUsers, onClose }) => {
  const dispatch = useDispatch();
  const site = useSelector(getActiveSite);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [stateChangeSettings, setStateChangeSettings] = useState({
    ...DEFAULT_STATE_CHANGE_SETTINGS,
  });
  const [showMatchesRoleWarning, setShowMatchesRoleWarning] = useState(false);
  const [matchingUsers, setMatchingUsers] = useState([]);
  const [customUsers, setCustomUsers] = useState([]);

  const userOptions = useMemo(() => {
    if (!siteUsers) return [];
    return siteUsers
      .filter((u) => !u.hasCustomConfig)
      .map((u) => ({
        value: u.id,
        label: u.user,
      }));
  }, [siteUsers]);

  const doCreate = (usersToCreate) => {
    const createList = usersToCreate || selectedUsers;
    for (const user of createList) {
      dispatch(
        createNotificationConfig({
          eventType: 'dtmp',
          user: user.value,
          emails: [user.label],
          org: site.org,
          site: site.id,
          stateChangeSettings,
          enabled: true,
        })
      );
    }
    onClose();
  };

  const handleSave = () => {
    // Split users into those whose settings match role defaults vs those that are custom
    const matching = [];
    const custom = [];

    for (const selected of selectedUsers) {
      const siteUser = siteUsers.find((u) => u.id === selected.value);
      if (siteUser && !isConfigCustom(siteUser.roleName, { stateChangeSettings })) {
        matching.push(selected);
      } else {
        custom.push(selected);
      }
    }

    if (matching.length > 0) {
      setMatchingUsers(matching);
      setCustomUsers(custom);
      setShowMatchesRoleWarning(true);
      return;
    }

    doCreate();
  };

  const handleMatchesRoleConfirm = () => {
    // Save only the custom users, skip the matching ones
    if (customUsers.length > 0) {
      doCreate(customUsers);
    } else {
      onClose();
    }
  };

  const canSave = selectedUsers.length > 0;

  return (
    <SimpleModal
      title="Add customization for user"
      size="m"
      onClose={onClose}
    >
      <div className="add-customization-modal">
        <div className="add-customization-modal__field">
          <label>Select user</label>
          <Select
            isMulti
            options={userOptions}
            value={selectedUsers}
            onChange={(value) => setSelectedUsers(value || [])}
            placeholder="Search and select users..."
          />
        </div>

        <div className="add-customization-modal__section-title">Set up notification rules</div>

        {selectedUsers.length > 0 ? (
          <NotificationRulesForm
            stateChangeSettings={stateChangeSettings}
            onChange={setStateChangeSettings}
          />
        ) : (
          <div className="add-customization-modal__placeholder">
            <Icon
              icon="he-info"
              size="xs"
            />
            <span>Select user to see notifications settings</span>
          </div>
        )}

        <div className="add-customization-modal__actions">
          <Button
            activity="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            activity="primary"
            onClick={handleSave}
            disabled={!canSave}
          >
            Save
          </Button>
        </div>
      </div>

      {showMatchesRoleWarning && (
        <ConfirmationDialog
          title="Settings match role defaults"
          body={
            <>
              <p>
                The following user{matchingUsers.length > 1 ? 's' : ''} will not be added because
                the settings <strong>match the role default settings</strong>:
              </p>
              <ul className="matches-role-list">
                {matchingUsers.map((u) => (
                  <li key={u.value}>
                    <strong>{u.label}</strong>
                  </li>
                ))}
              </ul>
              {customUsers.length > 0 ? (
                <>
                  <p>
                    The following user{customUsers.length > 1 ? 's' : ''} will be saved with custom
                    settings:
                  </p>
                  <ul className="matches-role-list">
                    {customUsers.map((u) => (
                      <li key={u.value}>
                        <strong>{u.label}</strong>
                      </li>
                    ))}
                  </ul>
                  <p>Do you want to proceed?</p>
                </>
              ) : (
                <p>
                  Please close this dialog and adjust the settings to differ from the role defaults
                  before saving.
                </p>
              )}
            </>
          }
          confirmText={customUsers.length > 0 ? 'Save' : 'Cancel'}
          confirmType={customUsers.length > 0 ? 'primary' : 'secondary'}
          showCancel={customUsers.length > 0}
          onConfirm={
            customUsers.length > 0
              ? handleMatchesRoleConfirm
              : () => setShowMatchesRoleWarning(false)
          }
          onCancel={() => setShowMatchesRoleWarning(false)}
          className="size-s"
        />
      )}
    </SimpleModal>
  );
};

export default AddCustomizationModal;
